import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveTimeZone } from "../utils/datetime";
import { tournamentsApi } from "../api/tournaments";
import { playersApi } from "../api/players";
import { clubsApi } from "../api/clubs";
import { activityLogsApi } from "../api/activity-logs";
import { matchesApi } from "../api/matches";
import { standingsApi } from "../api/standings";
import { formatTeamsApi } from "../api/format-teams";
import { statisticsApi } from "../api/statistics";
import { eventsApi } from "../api/events";
import { registrationApi } from "../api/registration";
import { prizesAwardsApi } from "../api/prizes-awards";
import type { CreateFormatTeamsRequest } from "../api/format-teams/types";
import type { GetTournamentsParams } from "../api/tournaments/types";
import type { PlayerPosition } from "../api/players/types";
import type { GetActivityLogsParams, GetTournamentActivityLogsParams } from "../api/activity-logs/types";
import type { GetStandingsResponse } from "../api/standings/types";
import type { SaveLineupRequest, SubstitutionRequest } from "../api/matches/types";

// Query Keys
export const queryKeys = {
  formatTeams: {
    all: ["formatTeams"] as const,
    byFormat: (formatId: string) => ["formatTeams", "byFormat", formatId] as const,
  },
  tournaments: {
    all: ["tournaments"] as const,
    list: (params?: GetTournamentsParams) => ["tournaments", "list", params] as const,
    detail: (id: string) => ["tournaments", "detail", id] as const,
    users: (id: string) => ["tournaments", "users", id] as const,
    invitations: (id: string) => ["tournaments", "invitations", id] as const,
    userRoles: (tournamentId: string, userId: string) => ["tournaments", "userRoles", tournamentId, userId] as const,
    formatBlocks: (id: string) => ["tournaments", "formatBlocks", id] as const,
    constraints: (id: string) => ["tournaments", "constraints", id] as const,
    dashboard: (id: string) => ["tournaments", "dashboard", id] as const,
  },
  players: {
    all: ["players"] as const,
    list: (tournamentId: string, params?: object) => ["players", "list", tournamentId, params] as const,
    detail: (id: string) => ["players", "detail", id] as const,
    byClub: (clubId: string) => ["players", "byClub", clubId] as const,
    withoutClub: (tournamentId?: string) => ["players", "withoutClub", tournamentId] as const,
  },
  clubs: {
    all: ["clubs"] as const,
    list: (tournamentId: string, params?: object) => ["clubs", "list", tournamentId, params] as const,
    detail: (id: string) => ["clubs", "detail", id] as const,
  },
  activityLogs: {
    all: ["activityLogs"] as const,
    list: (params?: GetActivityLogsParams) => ["activityLogs", "list", params] as const,
    tournament: (tournamentId: string, params?: GetTournamentActivityLogsParams) =>
      ["activityLogs", "tournament", tournamentId, params] as const,
  },
  matches: {
    all: ["matches"] as const,
    byFormat: (formatId: string) => ["matches", "byFormat", formatId] as const,
    detail: (matchId: string) => ["matches", "detail", matchId] as const,
  },
  registration: {
    settings: (tournamentId: string) => ["registration", "settings", tournamentId] as const,
    list: (tournamentId: string, status?: string) =>
      ["registration", "list", tournamentId, status ?? "all"] as const,
  },
  events: {
    all: ["events"] as const,
    byMatch: (matchId: string) => ["events", "byMatch", matchId] as const,
  },
  lineups: {
    all: ["lineups"] as const,
    byMatch: (matchId: string) => ["lineups", "byMatch", matchId] as const,
  },
  standings: {
    all: ["standings"] as const,
    byFormat: (formatId: string, live: boolean) => ["standings", "byFormat", formatId, { live }] as const,
  },
  userInvitations: ["userInvitations"] as const,
  statistics: {
    master: ["statistics", "master"] as const,
    tournament: (tournamentId: string) => ["statistics", "tournament", tournamentId] as const,
    playerLeaderboard: (tournamentId: string) => ["statistics", "playerLeaderboard", tournamentId] as const,
    clubLeaderboard: (tournamentId: string) => ["statistics", "clubLeaderboard", tournamentId] as const,
    analytics: (tournamentId: string, type: string) => ["statistics", "analytics", tournamentId, type] as const,
    goldenAward: (tournamentId: string, type: string) => ["statistics", "goldenAward", tournamentId, type] as const,
    rankedPlayerLeaderboard: (tournamentId: string, statisticId: number) =>
      ["statistics", "rankedPlayerLeaderboard", tournamentId, statisticId] as const,
    averages: (tournamentId: string) => ["statistics", "averages", tournamentId] as const,
    teamHighlight: (tournamentId: string, type: string) => ["statistics", "teamHighlight", tournamentId, type] as const,
    clubMatchStats: (tournamentId: string) => ["statistics", "clubMatchStats", tournamentId] as const,
    clubStandings: (tournamentId: string) => ["statistics", "clubStandings", tournamentId] as const,
    matchPlayers: (matchId: string) => ["statistics", "matchPlayers", matchId] as const,
    trend: (
      tournamentId: string,
      scope: string,
      statisticIds: string,
      playerId?: string,
      teamId?: string,
    ) => ["statistics", "trend", tournamentId, scope, statisticIds, playerId, teamId] as const,
  },
  venues: {
    byTournament: (tournamentId: string) => ["venues", "tournament", tournamentId] as const,
  },
};

// ============ TOURNAMENTS ============

export const useTournaments = (params?: GetTournamentsParams) => {
  return useQuery({
    queryKey: queryKeys.tournaments.list(params),
    queryFn: () => tournamentsApi.getTournaments(params),
  });
};

export const useTournamentDetails = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.detail(tournamentId),
    queryFn: () => tournamentsApi.getTournamentDetails(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useTournamentUsers = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.users(tournamentId),
    queryFn: () => tournamentsApi.getTournamentUsers(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useTournamentPendingInvitations = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.invitations(tournamentId),
    queryFn: () => tournamentsApi.getTournamentPendingInvitations(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useUserRolesInTournament = (tournamentId: string, userId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.userRoles(tournamentId, userId),
    queryFn: () => tournamentsApi.getUserRolesInTournament(tournamentId, userId),
    enabled: !!tournamentId && !!userId,
  });
};

export const useTournamentFormatBlocks = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.formatBlocks(tournamentId),
    queryFn: () => tournamentsApi.getTournamentFormatBlocks(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useTournamentConstraints = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.constraints(tournamentId),
    queryFn: () => tournamentsApi.getTournamentConstraints(tournamentId),
    enabled: !!tournamentId,
    retry: false, // Don't retry on 404, as it means constraints don't exist
  });
};

/**
 * The zone a tournament's fixtures are scheduled in.
 *
 * Match start times come back as absolute instants; formatting them with the
 * browser's zone shows the wrong local time to anyone outside the tournament's
 * country. Falls back to the same default the backend uses, so it is safe to
 * read before the constraints have loaded.
 */
export const useTournamentTimeZone = (tournamentId: string): string => {
  const { data } = useTournamentConstraints(tournamentId);
  return resolveTimeZone(data?.timezone);
};

export const useTournamentDashboard = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.tournaments.dashboard(tournamentId),
    queryFn: () => tournamentsApi.getTournamentDashboard(tournamentId),
    enabled: !!tournamentId,
    staleTime: 0,
  });
};

export const useUserInvitations = () => {
  return useQuery({
    queryKey: queryKeys.userInvitations,
    queryFn: () => tournamentsApi.getUserInvitations(),
  });
};

// Tournament Mutations
export const useCreateTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: tournamentsApi.createTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

export const useJoinTournament = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: tournamentsApi.joinTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.userInvitations });
    },
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: tournamentsApi.inviteUser,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.invitations(variables.tournamentId) });
    },
  });
};

export const useRespondToInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: tournamentsApi.respondToInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userInvitations });
      queryClient.invalidateQueries({ queryKey: queryKeys.tournaments.all });
    },
  });
};

// ============ PLAYERS ============

export const useTournamentPlayers = (
  tournamentId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: "name" | "createdAt" | "position" | "number" | "dateOfBirth" | "gender" | "clubName";
    sortOrder?: "asc" | "desc";
    clubId?: string;
    userId?: string;
    gender?: "male" | "female" | "other";
    position?: PlayerPosition;
    isManual?: boolean;
    minAge?: number;
    maxAge?: number;
  },
) => {
  return useQuery({
    queryKey: queryKeys.players.list(tournamentId, params),
    queryFn: () => playersApi.getPlayersByTournament(tournamentId, params),
    enabled: !!tournamentId,
  });
};

export const usePlayerDetail = (playerId: string) => {
  return useQuery({
    queryKey: queryKeys.players.detail(playerId),
    queryFn: () => playersApi.getTournamentPlayer(playerId),
    enabled: !!playerId,
  });
};

export const usePlayersByClub = (clubId: string) => {
  return useQuery({
    queryKey: queryKeys.players.byClub(clubId),
    queryFn: () => playersApi.getPlayersByClub(clubId),
    enabled: !!clubId,
  });
};

export const usePlayersWithoutClub = (tournamentId?: string) => {
  return useQuery({
    queryKey: queryKeys.players.withoutClub(tournamentId),
    queryFn: () => playersApi.getPlayersWithoutClub(tournamentId),
  });
};

// Player Mutations
export const useCreatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: playersApi.createTournamentPlayer,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.list(variables.tournamentId!) });
    },
  });
};

export const useUpdatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof playersApi.updateTournamentPlayer>[1] }) =>
      playersApi.updateTournamentPlayer(id, data),
    onSuccess: (player) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.detail(player.id) });
      if (player.tournamentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.players.list(player.tournamentId) });
      }
    },
  });
};

export const useDeletePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: playersApi.deleteTournamentPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
    },
  });
};

// ============ CLUBS (TEAMS) ============

export const useTournamentClubs = (
  tournamentId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    minPlayers?: number;
    maxPlayers?: number;
    createdAtFrom?: string;
    createdAtTo?: string;
    sortBy?: "name" | "captain" | "playerCount" | "createdAt";
    direction?: "asc" | "desc";
  },
) => {
  return useQuery({
    queryKey: queryKeys.clubs.list(tournamentId, params),
    queryFn: () => clubsApi.getClubsByTournament(tournamentId, params),
    enabled: !!tournamentId,
  });
};

export const useClubDetail = (clubId: string) => {
  return useQuery({
    queryKey: queryKeys.clubs.detail(clubId),
    queryFn: () => clubsApi.getTournamentClubDetail(clubId),
    enabled: !!clubId,
  });
};

// Club Mutations
export const useCreateClub = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clubsApi.createTournamentClub,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
    },
  });
};

export const useUpdateClub = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof clubsApi.updateTournamentClub>[1] }) =>
      clubsApi.updateTournamentClub(id, data),
    onSuccess: (club) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(club.id) });
      if (club.tournamentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.clubs.list(club.tournamentId) });
      }
    },
  });
};

export const useDeleteClub = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clubsApi.deleteTournamentClub,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
    },
  });
};

export const useAssignCaptain = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clubId, playerId }: { clubId: string; playerId: string }) =>
      clubsApi.assignCaptain(clubId, playerId),
    onSuccess: (club) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(club.id) });
      if (club.tournamentId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.clubs.list(club.tournamentId) });
      }
    },
  });
};

// ============ ACTIVITY LOGS ============

export const useActivityLogs = (params?: GetActivityLogsParams) => {
  return useQuery({
    queryKey: queryKeys.activityLogs.list(params),
    queryFn: () => activityLogsApi.getActivityLogs(params),
  });
};

export const useTournamentActivityLogs = (tournamentId: string, params?: GetTournamentActivityLogsParams) => {
  return useQuery({
    queryKey: queryKeys.activityLogs.tournament(tournamentId, params),
    queryFn: () => activityLogsApi.getTournamentActivityLogs(tournamentId, params),
    enabled: !!tournamentId,
  });
};

// ============ MATCHES ============

export const useMatchDetails = (matchId?: string | null) => {
  return useQuery({
    queryKey: matchId ? queryKeys.matches.detail(matchId) : queryKeys.matches.all,
    queryFn: () => matchesApi.getMatchDetails(matchId!),
    enabled: !!matchId,
  });
};

export const useMatchesByFormat = (formatId?: string | null) => {
  return useQuery({
    queryKey: formatId ? queryKeys.matches.byFormat(formatId) : queryKeys.matches.all,
    queryFn: () => matchesApi.getMatchesByFormat(formatId!),
    enabled: !!formatId,
  });
};

// ============ STANDINGS ============

export const useStandingsByFormat = (formatId?: string | null, live: boolean = false, enabled: boolean = true) => {
  return useQuery<GetStandingsResponse>({
    queryKey: formatId ? queryKeys.standings.byFormat(formatId, live) : queryKeys.standings.all,
    queryFn: () => standingsApi.getStandingsByFormat(formatId!, live),
    enabled: enabled && !!formatId,
  });
};

// ============ STATISTICS ============

export const useMasterStatistics = () => {
  return useQuery({
    queryKey: queryKeys.statistics.master,
    queryFn: () => statisticsApi.getMasterStatistics(),
  });
};

export const useTournamentStatistics = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.statistics.tournament(tournamentId),
    queryFn: () => statisticsApi.getTournamentStatistics(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useEnableTournamentStatistics = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: statisticsApi.enableTournamentStatistics,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.statistics.tournament(variables.tournamentId) });
    },
  });
};

export const usePlayerLeaderboard = (tournamentId: string, statisticIds?: string) => {
  return useQuery({
    queryKey: queryKeys.statistics.playerLeaderboard(tournamentId),
    queryFn: () => statisticsApi.getPlayerLeaderboard({ tournamentId, statisticIds }),
    enabled: !!tournamentId,
  });
};

export const useClubLeaderboard = (tournamentId: string, statisticIds?: string) => {
  return useQuery({
    queryKey: queryKeys.statistics.clubLeaderboard(tournamentId),
    queryFn: () => statisticsApi.getClubLeaderboard({ tournamentId, statisticIds }),
    enabled: !!tournamentId,
  });
};

export const useAnalytics = (tournamentId: string, type: import("api/statistics/types").AnalyticsType) => {
  return useQuery({
    queryKey: queryKeys.statistics.analytics(tournamentId, type),
    queryFn: () => statisticsApi.getAnalytics(tournamentId, type),
    enabled: !!tournamentId,
  });
};

export const useGoldenAward = (tournamentId: string, type: import("api/statistics/types").GoldenAwardType) => {
  return useQuery({
    queryKey: queryKeys.statistics.goldenAward(tournamentId, type),
    queryFn: () => statisticsApi.getGoldenAward(tournamentId, type),
    enabled: !!tournamentId,
  });
};

export const useAverages = (tournamentId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.statistics.averages(tournamentId),
    queryFn: () => statisticsApi.getAverages(tournamentId),
    enabled: enabled && !!tournamentId,
  });
};

export const useTeamHighlight = (tournamentId: string, type: import("api/statistics/types").TeamHighlightType) => {
  return useQuery({
    queryKey: queryKeys.statistics.teamHighlight(tournamentId, type),
    queryFn: () => statisticsApi.getTeamHighlight(tournamentId, type),
    enabled: !!tournamentId,
  });
};

export const useClubMatchStats = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.statistics.clubMatchStats(tournamentId),
    queryFn: () => statisticsApi.getClubMatchStats(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useClubStandings = (tournamentId: string) => {
  return useQuery({
    queryKey: queryKeys.statistics.clubStandings(tournamentId),
    queryFn: () => statisticsApi.getClubStandings(tournamentId),
    enabled: !!tournamentId,
  });
};

export const useStatisticsTrend = (
  tournamentId: string,
  scope: import("api/statistics/types").TrendScope,
  statisticIds: string,
  playerId?: string,
  teamId?: string,
) => {
  return useQuery({
    queryKey: queryKeys.statistics.trend(tournamentId, scope, statisticIds, playerId, teamId),
    queryFn: () =>
      statisticsApi.getTrend({ tournamentId, scope, statisticIds, playerId, teamId }),
    enabled:
      !!tournamentId &&
      statisticIds.length > 0 &&
      (scope !== "player" || !!playerId) &&
      (scope !== "team" || !!teamId),
  });
};

export const useRankedPlayerLeaderboard = (tournamentId: string, statisticId: number, order?: "asc" | "desc") => {
  return useQuery({
    queryKey: queryKeys.statistics.rankedPlayerLeaderboard(tournamentId, statisticId),
    queryFn: () => statisticsApi.getRankedPlayerLeaderboard({ tournamentId, statisticId, order }),
    enabled: !!tournamentId && statisticId > 0,
  });
};

// ============ FORMAT TEAMS ============

export const useFormatTeams = (formatId?: string | null) => {
  return useQuery({
    queryKey: formatId ? queryKeys.formatTeams.byFormat(formatId) : queryKeys.formatTeams.all,
    queryFn: () => formatTeamsApi.getFormatTeams(formatId!),
    enabled: !!formatId,
  });
};

export const useCreateFormatTeams = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof formatTeamsApi.createFormatTeams>[0]) =>
      formatTeamsApi.createFormatTeams(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.formatTeams.byFormat(variables.formatId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.formatTeams.all });
    },
  });
};

export const useDeleteFormatTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ formatTeamId, formatId }: { formatTeamId: string; formatId?: string }) =>
      formatTeamsApi.deleteFormatTeam(formatTeamId).then(() => ({ formatId })),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.formatTeams.all });
      if (variables.formatId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.formatTeams.byFormat(variables.formatId) });
      }
    },
  });
};

// ============================================================================
// Match events (live logging)
// ============================================================================

export const useMatchEvents = (matchId: string | undefined) => {
  return useQuery({
    queryKey: matchId ? queryKeys.events.byMatch(matchId) : queryKeys.events.all,
    queryFn: () => eventsApi.getEventsByMatch(matchId!),
    enabled: !!matchId,
  });
};

/*
  Logging an event moves the match score too — the backend recomputes the match
  unit from its goal events — so the match detail and standings caches are stale
  the moment an event lands, not just the events list.
*/
const invalidateAfterEventChange = (
  queryClient: ReturnType<typeof useQueryClient>,
  matchId: string
) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.events.byMatch(matchId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.matches.detail(matchId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.standings.all });
};

export const useCreateEvent = (matchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: eventsApi.createEvent,
    onSuccess: () => invalidateAfterEventChange(queryClient, matchId),
  });
};

export const useDeleteEvent = (matchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: eventsApi.deleteEvent,
    onSuccess: () => invalidateAfterEventChange(queryClient, matchId),
  });
};

export const useSwapEventOrder = (matchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: eventsApi.swapDisplayOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.byMatch(matchId) });
    },
  });
};

// ============================================================================
// Match lineups (team sheets and substitutions)
// ============================================================================

export const useMatchLineups = (matchId: string | undefined) => {
  return useQuery({
    queryKey: matchId ? queryKeys.lineups.byMatch(matchId) : queryKeys.lineups.all,
    queryFn: () => matchesApi.getMatchLineups(matchId!),
    enabled: !!matchId,
  });
};

/* Both write endpoints answer with the whole match's sheets, so the cache is set from the response rather than refetched. */
export const useSaveLineup = (matchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveLineupRequest) => matchesApi.saveLineup(matchId, data),
    onSuccess: (lineups) => queryClient.setQueryData(queryKeys.lineups.byMatch(matchId), lineups),
  });
};

export const useSubstitute = (matchId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubstitutionRequest) => matchesApi.substitute(matchId, data),
    onSuccess: (lineups) => queryClient.setQueryData(queryKeys.lineups.byMatch(matchId), lineups),
  });
};

export const useMatchPlayerStatistics = (matchId: string | undefined) => {
  return useQuery({
    queryKey: matchId ? queryKeys.statistics.matchPlayers(matchId) : queryKeys.statistics.master,
    queryFn: () => statisticsApi.getMatchPlayerStatistics({ matchId: matchId! }),
    enabled: !!matchId,
  });
};

// ============================================================================
// Registration settings
// ============================================================================

export const useRegistrationSettings = (tournamentId: string | undefined) => {
  return useQuery({
    queryKey: tournamentId
      ? queryKeys.registration.settings(tournamentId)
      : queryKeys.registration.settings("none"),
    queryFn: () => registrationApi.getSettings(tournamentId!),
    enabled: !!tournamentId,
  });
};

export const usePrizes = (tournamentId?: string) =>
  useQuery({
    queryKey: ["tournament", tournamentId, "prizes"],
    queryFn: () => prizesAwardsApi.getPrizes(tournamentId!),
    enabled: !!tournamentId,
  });

export const useAwards = (tournamentId?: string) =>
  useQuery({
    queryKey: ["tournament", tournamentId, "awards"],
    queryFn: () => prizesAwardsApi.getAwards(tournamentId!),
    enabled: !!tournamentId,
  });

export const useSavePrizes = (tournamentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (prizes: Parameters<typeof prizesAwardsApi.savePrizes>[1]) =>
      prizesAwardsApi.savePrizes(tournamentId, prizes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId, "prizes"] });
    },
  });
};

export const useSaveAwards = (tournamentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (awards: Parameters<typeof prizesAwardsApi.saveAwards>[1]) =>
      prizesAwardsApi.saveAwards(tournamentId, awards),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tournament", tournamentId, "awards"] });
    },
  });
};

export const useSaveRegistrationSettings = (tournamentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof registrationApi.saveSettings>[1]) =>
      registrationApi.saveSettings(tournamentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.registration.settings(tournamentId) });
    },
  });
};

export const useRegistrations = (
  tournamentId: string | undefined,
  status?: import("api/registration/types").RegistrationStatus
) => {
  return useQuery({
    queryKey: queryKeys.registration.list(tournamentId ?? "none", status),
    queryFn: () => registrationApi.listRegistrations(tournamentId!, status),
    enabled: !!tournamentId,
  });
};

export const useDecideRegistration = (tournamentId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      registrationId,
      data,
    }: {
      registrationId: string;
      data: import("api/registration/types").DecideRegistrationRequest;
    }) => registrationApi.decideRegistration(tournamentId, registrationId, data),
    onSuccess: () => {
      /* A decision moves an entry between tabs and, on approval, creates a club
         and its players — so the squad lists are stale too. */
      queryClient.invalidateQueries({ queryKey: ["registration", "list", tournamentId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.players.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });
    },
  });
};
